/**
 * InterestListsSettings (/crm/settings/interest-lists) — the future-study
 * interest list catalog (owner request 2026-08-20): the standard study types
 * the site regularly gets (T2DM no injectables, migraine, vaccine, healthy
 * obesity, gout, CV outcomes, …). Recruiters put leads on these lists from the
 * lead's Profile tab or the close-out dialog; when a study in that indication
 * opens, "View leads" is the call list.
 *
 * Read = every CRM user (the counts answer "do we have a bench for this?");
 * curation (create / rename / retire) = CRM admin, the profile-keys gate.
 * Lists are retired, never deleted — the membership history stays.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookmarkPlus, Loader2, Pencil, Plus, Users } from 'lucide-react';
import api from '../../../services/api';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import Modal from '../../components/forms/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import { useIsCRMAdmin } from '../../lib';
import { Alert, Badge } from '../../components/ui';
import type { InterestCategory } from '../../types';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function EditListModal({
  category,
  onClose,
  onSaved,
}: {
  /** null = create a new list. */
  category: InterestCategory | null;
  onClose: () => void;
  onSaved: (saved: InterestCategory, existed: boolean) => void;
}) {
  const [label, setLabel] = useState(category?.label || '');
  const [description, setDescription] = useState(category?.description || '');
  const [isActive, setIsActive] = useState(category?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (busy) return;
    const trimmed = label.trim();
    if (!trimmed) { setError('Give the list a name.'); return; }
    setBusy(true);
    setError(null);
    try {
      if (category) {
        const res = await api.patch(`/api/crm/interest-categories/${category.id}`, {
          label: trimmed,
          description: description.trim() || null,
          is_active: isActive,
        });
        onSaved(res.data.category, false);
      } else {
        const res = await api.post('/api/crm/interest-categories', {
          label: trimmed,
          description: description.trim() || undefined,
        });
        onSaved(res.data.category, Boolean(res.data.existed));
      }
    } catch (e: any) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen title={category ? 'Edit future-study list' : 'New future-study list'} onClose={onClose} maxWidth="max-w-md">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Name</label>
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={120}
            placeholder="e.g. T2DM (GLP-1 naive)"
            className={`${fieldCls} w-full`}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Description <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="What profile belongs here — helps recruiters pick the right list."
            className={`${fieldCls} w-full resize-none`}
          />
        </div>
        {category && (
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
            />
            Active — offered in the pickers
            <span className="text-xs font-normal text-gray-400">(retiring keeps everyone already on it)</span>
          </label>
        )}
        {error && <Alert tone="error" size="sm">{error}</Alert>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={save} disabled={busy || !label.trim()} className={primaryBtnCls}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {category ? 'Save' : 'Create list'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function InterestListsSettings() {
  const navigate = useNavigate();
  const isCrmAdmin = useIsCRMAdmin();
  const [categories, setCategories] = useState<InterestCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<InterestCategory | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/crm/interest-categories?counts=true');
      setCategories(res.data?.categories || []);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load(); };

  const onSaved = () => {
    setEditing(null);
    setCreating(false);
    setRefreshing(true);
    load();
  };

  const viewLeads = (c: InterestCategory) =>
    navigate(`/crm/database?interest=${encodeURIComponent(c.key)}&interest_name=${encodeURIComponent(c.label)}`);

  return (
    <div className="space-y-4">
      <PageHeader
        icon={BookmarkPlus}
        title="Future-study lists"
        count={categories.length || null}
        subtitle="Standard study types we regularly get. Park a promising lead on one — when that study opens, the list is your call sheet."
        actions={
          <>
            <RefreshButton onClick={refresh} loading={refreshing} />
            {isCrmAdmin && (
              <button type="button" onClick={() => setCreating(true)} className={primaryBtnCls}>
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New list</span>
              </button>
            )}
          </>
        }
      />

      {error && (
        <Alert tone="error">
          {error}{' '}
          <button onClick={() => { setLoading(true); load(); }} className="font-medium underline">Retry</button>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No lists yet.{isCrmAdmin ? ' Create the study types you regularly run.' : ''}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">List</th>
                <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 sm:table-cell">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Leads</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {categories.map((c) => (
                <tr key={c.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.label}</span>
                      {!c.is_active && <Badge tone="gray">Retired</Badge>}
                    </div>
                    <p className="mt-0.5 font-mono text-[11px] text-gray-400 dark:text-gray-500">{c.key}</p>
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-gray-500 dark:text-gray-400 sm:table-cell">
                    {c.description || <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
                    {(c.member_count ?? 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => viewLeads(c)}
                        disabled={!c.member_count}
                        title={c.member_count ? 'Open these leads in the patient database' : 'Nobody on this list yet'}
                        className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
                      >
                        <Users className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">View leads</span>
                      </button>
                      {isCrmAdmin && (
                        <button
                          type="button"
                          onClick={() => setEditing(c)}
                          aria-label={`Edit ${c.label}`}
                          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Leads land on these lists from the lead page (Profile tab → Future studies) or the
        close-out dialog. Removing a lead never deletes the history — the timeline keeps both.
      </p>

      {(creating || editing) && (
        <EditListModal
          category={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
